// src/worlds/ceramics/components/help/HelpCenterModal.jsx
import React, { useState } from "react";
import { createPortal } from "react-dom";
import { WIDGET_REGISTRY } from "../../../../lib/widgets";

export default function HelpCenterModal({ isOpen, onClose }) {
  const [activeTab, setActiveTab] = useState("faq"); // "faq" | "widgets"
  if (!isOpen) return null;

  const faqItems = [
    {
      id: "what-is-mudo",
      question: "What is Mudo?",
      answer:
        "Mudo is a comprehensive studio management platform for ceramic artists and pottery makers. It combines a Studio world (for managing your business, costs, team, and production) with a Visitor world (for discovering other studios, booking workshops, and accessing the community marketplace). Think of it as your all-in-one business dashboard plus a community hub for ceramics.",
    },
    {
      id: "studio-vs-visitor",
      question: "What's the difference between Studio world and Visitor world?",
      answer:
        "Studio world is your private control center—manage your inventory, costs, staff, schedules, workshops, and studio data. It requires an active trial or subscription. Visitor world is the public-facing side—explore other studios on the Mudo Map, book workshops, discover makers, and browse the community marketplace. Visitor world is always free to explore.",
    },
    {
      id: "how-to-access-studio",
      question: "How do I access my Studio world?",
      answer:
        "When you log in, you'll see a role selection screen. Choose 'Studio' to enter your studio workspace. Your first login automatically starts a 7-day trial, giving you full access to all studio features. After 7 days, you can enter a promo code (if you have one) or choose a paid subscription to continue using the studio.",
    },
    {
      id: "trial-explained",
      question: "How does the 7-day trial work?",
      answer:
        "Your trial automatically starts on your first login and gives you 7 days of full access to all studio features. The countdown pill in the top bar shows how many days remain. When your trial ends, you'll see a message saying your access has ended. At that point, you can either enter a promo code for extended access or choose a paid subscription plan.",
    },
    {
      id: "promo-codes",
      question: "What are promo codes and how do I use them?",
      answer:
        "Promo codes extend your access without paying. Examples include BLS-FREE (lifetime access), MUDOFREE-1M (free for 1 month), or discount codes like MUDO25-1M (25% off for 1 month). When your trial ends or access expires, click 'Choose Plan' and look for the promo code input field. Each code can only be used once per account. After applying a code, your access will extend and the countdown pill will update.",
    },
    {
      id: "data-storage",
      question: "Where does Mudo store my data?",
      answer:
        "Mudo saves your studio data to Supabase (a secure cloud database). Your data is backed up and synced across devices when you log in. You can export your data anytime from Data & Backup settings. All your information is encrypted and secure.",
    },
    {
      id: "data-persistence",
      question: "How is my data saved and persisted?",
      answer:
        "Every change you make (adding tasks, logging costs, updating inventory) is automatically saved to your account. Your data persists across sessions—log out and log back in anytime, and all your information will be there. You can also export backups for safekeeping from the Data & Backup section.",
    },
    {
      id: "studio-profile",
      question: "What should I put in my Studio Profile?",
      answer:
        "Your studio profile includes your name, location, description, contact info, and photo. This info appears on the Mudo Map if you make your studio public, and helps potential workshop students find you. Keep it updated so visitors can easily locate and contact you.",
    },
    {
      id: "dashboard-widgets",
      question: "What are Dashboard Widgets and how do I customize them?",
      answer:
        "Widgets are cards that show useful information and tools on your dashboard—like Cost Planner, Streak tracker, Journal, Kiln logs, etc. You can customize which widgets appear by clicking 'Manage Widgets' in your Dashboard settings. Turn widgets on/off, and arrange them however you like.",
    },
    {
      id: "organize-studio",
      question: "How should I organize my Studio data?",
      answer:
        "Start with the basics: (1) Set up your studio profile and public location, (2) Add your staff and their roles, (3) Create a materials inventory, (4) Set up your pricing structure in Cost Calculator, (5) Start logging tasks and production in Planner. Use categories and tags to organize everything. The more detail you add, the better insights you'll get.",
    },
    {
      id: "track-costs",
      question: "How do I track my ceramic production costs?",
      answer:
        "Use the Cost Planner widget. Log materials (clay, glazes, tools), labor hours, and overhead. Assign costs to specific pieces or batches. View your total costs and estimated margins. This helps you price your work accurately and understand your profitability.",
    },
    {
      id: "manage-inventory",
      question: "How do I manage my materials inventory?",
      answer:
        "In the Materials section, add all your materials (clay types, glazes, tools, packaging). Log quantities and storage locations. Track usage as you use materials. Set minimum levels to get notified when stock is low. This ensures you never run out of essential supplies.",
    },
    {
      id: "plan-production",
      question: "How do I plan my ceramic production schedule?",
      answer:
        "Use the Planner widget to create tasks and production schedules. Add deadlines, assign to team members, and track progress. Use Gantt view to visualize timelines. Log actual production in the Production widget to see what you completed vs. what you planned.",
    },
    {
      id: "host-workshop",
      question: "How do I set up and host workshops?",
      answer:
        "Go to Workshops in your studio. Create a new workshop with title, description, date, time, duration, price, and capacity. Set your location or allow remote attendance. Students can book from the Visitor world or Mudo Map. You'll see registrations and can manage attendance.",
    },
    {
      id: "offer-studio-space",
      question: "Can I rent out my studio space?",
      answer:
        "Yes! In Studio Booking, you can list your studio or workspace for rent. Set availability, pricing per hour/day, capacity, and amenities. Visitors can book directly through the Mudo Map. Bookings are managed in your calendar, and payments go through Stripe (you need Stripe Connect set up for payouts).",
    },
    {
      id: "use-mudo-map",
      question: "What is the Mudo Map and how do I use it?",
      answer:
        "The Mudo Map shows ceramic studios, makers, workshops, and spaces across the region. As a Studio owner, your location appears on the map (if you enable public visibility in settings). Visitors can see your studio, workshop offerings, and space bookings. Use it to discover other makers, find complementary businesses, or find customers.",
    },
    {
      id: "share-studio-link",
      question: "How do I share my studio with others?",
      answer:
        "In your Studio Profile settings, you can enable 'Make Studio Public' to appear on the Mudo Map. Share your direct studio link with friends, customers, or students. They can see your public info, book workshops, or reserve studio space without needing an account.",
    },
    {
      id: "understand-streak",
      question: "What is the Streak feature and why use it?",
      answer:
        "Streak tracks your daily activity and creative consistency. Each day you complete an action (task, journal entry, production log, etc.), you build a streak. It's a motivational tool to encourage regular creative practice. See your current streak and personal best on the dashboard.",
    },
    {
      id: "use-journal",
      question: "How do I use the Journal feature?",
      answer:
        "The Journal is your creative notebook. Write reflections, log experimental firings, document glaze tests, capture process notes, or share ideas. Attach photos of your work. Tag entries by technique or theme. Review past entries for inspiration and lessons learned.",
    },
    {
      id: "track-kiln",
      question: "How do I track my kiln activity and maintenance?",
      answer:
        "Use the Kiln Tracker widget to log each firing (temperature, duration, cone pack, results). Add notes about what worked or didn't. Track maintenance schedules and repairs. This data helps you improve your kiln management and ensure consistent results.",
    },
    {
      id: "subscription-renew",
      question: "What happens when my subscription expires?",
      answer:
        "When your subscription or trial ends, you'll see an access notification. You won't be able to enter your Studio world until you renew. Options: (1) Use a promo code if you have one, (2) Choose and pay for a subscription plan, or (3) Wait for another promo code. Your data is safe and will be there when you renew.",
    },
    {
      id: "cancel-subscription",
      question: "How do I cancel my subscription?",
      answer:
        "Go to Manage Billing in the top menu. You'll be taken to your billing portal where you can manage your subscription, view invoices, and cancel anytime. Cancellations take effect at the end of your current billing period.",
    },
    {
      id: "contact-support",
      question: "How do I contact support or report a bug?",
      answer:
        "Click 'Contact Us' in the top menu to send us a message. Tell us what's on your mind—questions, bugs, feature requests, or feedback. We review all messages and try to respond quickly.",
    },
    {
      id: "reset-password",
      question: "How do I reset my password?",
      answer:
        "On the login page, click 'Forgot password?' and enter your email. You'll receive a password reset link. Click it, enter your new password, and you're done. If you don't receive the email, check your spam folder.",
    },
  ];

  // Use authoritative widget registry (always in sync)
  const widgetsHelp = WIDGET_REGISTRY.map((w) => ({
    id: w.id,
    name: w.name,
    summary: w.description,
    howItWorks: w.howItWorks,
    tips: w.tips,
    requiresSubscription: w.requiresSubscription,
  }));

  return createPortal(
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="help-center-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className="relative bg-white/10 dark:bg-black/40 text-white rounded-xl p-6 w-full max-w-3xl mx-4 shadow-lg border border-white/20 overflow-auto max-h-[90vh]">
        <div className="mb-4 flex items-center justify-between gap-3">
          <h2 id="help-center-title" className="text-2xl font-bold">Help & FAQ</h2>
          <button
            className="rounded-full border border-white/20 bg-white/10 px-3 py-1 text-sm"
            onClick={onClose}
            title="Close"
          >
            Close
          </button>
        </div>
        <p className="text-sm text-white/80 mb-4">
          Learn how Mudo works, and what each widget on your dashboard does.
        </p>

        <div className="flex gap-2 mb-4">
          <TabButton active={activeTab === "faq"} onClick={() => setActiveTab("faq")}>FAQ</TabButton>
          <TabButton active={activeTab === "widgets"} onClick={() => setActiveTab("widgets")}>Widgets</TabButton>
        </div>

        {activeTab === "faq" ? (
          <div className="space-y-2">
            {faqItems.map((item) => (
              <details key={item.id} className="rounded-lg border border-white/20 bg-black/30 p-3">
                <summary className="cursor-pointer text-sm font-semibold text-white/90">
                  {item.question}
                </summary>
                <div className="mt-2 text-sm text-white/80">{item.answer}</div>
              </details>
            ))}
          </div>
        ) : (
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2">
            {widgetsHelp.map((w) => (
              <div 
                key={w.id} 
                className={`rounded-xl border p-4 ${
                  w.requiresSubscription
                    ? "border-orange-500/30 bg-orange-500/5"
                    : "border-white/20 bg-black/30"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <h3 className="text-sm font-semibold">{w.name}</h3>
                  {w.requiresSubscription && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-500/20 text-orange-300 whitespace-nowrap">
                      Pro
                    </span>
                  )}
                </div>
                <p className="text-xs text-white/80 mb-2">{w.summary}</p>
                <div className="mb-2">
                  <div className="text-xs font-semibold text-white/80">How it works</div>
                  <ul className="list-disc pl-5 text-xs text-white/80">
                    {w.howItWorks.map((h, i) => (
                      <li key={i}>{h}</li>
                    ))}
                  </ul>
                </div>
                {Array.isArray(w.tips) && w.tips.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-white/80">Tips</div>
                    <ul className="list-disc pl-5 text-xs text-white/80">
                      {w.tips.map((t, i) => (
                        <li key={i}>{t}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

function TabButton({ active, onClick, children }) {
  const base = "px-3 py-1.5 rounded-full text-sm border transition";
  const cls = active
    ? "bg-[var(--color-primary)]/90 border-[var(--color-primary)] text-white shadow"
    : "bg-black/30 border-white/20 text-white/80 hover:bg-white/10";
  return (
    <button type="button" onClick={onClick} className={`${base} ${cls}`}>
      {children}
    </button>
  );
}
